// Size gate (2026-08-09): a product's size must be chosen deliberately before it
// can be added to the cart.
//
// Why it exists: inventory is UNTRACKED store-wide, so every variant is always
// "available" and Shopify's `selected_or_first_available_variant` resolves to the
// first size in the ladder — size 3 on a boot, 2XS on a shirt. A customer who never
// touches the size pills still gets a fully valid, addable variant. That is a
// wrong-size order the storefront invited.
//
// ponytail: client-side only, and deliberately so. Nothing server-side can tell a
// deliberate size choice from a Liquid default — the DOM is the only place that
// fact exists. With JS off the page behaves exactly as it did before this file.
//
// Loaded globally from layout/theme.liquid rather than from main-product.liquid so
// it also covers the quick-add modal, which injects a second picker via innerHTML
// (where a section-local <script> would never execute).
(() => {
  const SIZE_SEL = '[data-mgp-size]';
  const GATE_LABEL = 'Select a size';

  // The scope that survives a variant re-render. product-info.js swaps the
  // <variant-selects> subtree but keeps <product-info> itself, so the "user has
  // chosen" flag is stored there and outlives every option change.
  const scopeOf = (el) => el.closest('product-info') || el.closest('.product') || document.body;

  const placeholderOf = (select) =>
    select.options[select.selectedIndex]?.hasAttribute('data-mgp-size-placeholder');

  // A single-value size is not a choice. The gate exists to stop Shopify's
  // first-in-the-ladder default becoming a wrong-size order; where the ladder
  // has one rung there is no wrong size, and gating only disables the buy button
  // until the shopper clicks the one value there is. 2,441 live products sat in
  // exactly that state — ERR-083, 2026-08-30.
  const sizeChoiceCount = (fieldset) => {
    const select = fieldset.querySelector('select');
    return select
      ? select.querySelectorAll('option:not([data-mgp-size-placeholder])').length
      : fieldset.querySelectorAll('input[type="radio"]').length;
  };

  // Gating must be a no-op once already applied. The MutationObserver below watches
  // the same subtree this function writes to, so an unconditional write would feed
  // itself a fresh mutation every frame and spin forever.
  function pending(scope) {
    const fieldset = scope.querySelector(SIZE_SEL);
    if (!fieldset || scope.dataset.mgpSizeChosen === '1') return null;
    if (sizeChoiceCount(fieldset) <= 1) return null;

    const select = fieldset.querySelector('select');
    const submit = scope.querySelector('.product-form__submit');
    const dirty =
      fieldset.querySelector('input[type="radio"]:checked') ||
      (select && !placeholderOf(select)) ||
      (submit && !submit.classList.contains('is-size-pending')) ||
      scope.querySelector('.shopify-payment-button:not([hidden])');

    return dirty ? { fieldset, select, submit } : null;
  }

  function gate(scope) {
    const work = pending(scope);
    if (!work) return;
    const { fieldset, select, submit } = work;

    fieldset.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      input.checked = false;
      input.removeAttribute('checked');
    });

    // A <select> cannot be "unselected", so a placeholder is prepended once and made
    // current. It carries no data-option-value-id, so VariantSelects'
    // `selectedOptionValues` getter reads it as undefined rather than a real size.
    if (select) {
      let placeholder = select.querySelector('[data-mgp-size-placeholder]');
      if (!placeholder) {
        placeholder = new Option(GATE_LABEL, '');
        placeholder.disabled = true;
        placeholder.setAttribute('data-mgp-size-placeholder', '');
        select.prepend(placeholder);
      }
      select.querySelectorAll('option[selected]').forEach((o) => o.removeAttribute('selected'));
      placeholder.setAttribute('selected', 'selected');
      select.selectedIndex = 0;
    }

    // The button stays a normal-looking "Add to cart" (2026-09-23: the old greyed
    // "Select a size" block read as broken). The add is blocked by the submit
    // intercept below, which points at the size row instead. Only a class is
    // written here: an attribute change is invisible to the childList observer,
    // so gating can never feed itself a mutation.
    if (submit) submit.classList.add('is-size-pending');

    // Express checkout bypasses the submit button entirely — it posts the hidden
    // variant id straight to Shop Pay / PayPal / Google Pay. Hiding it is the only
    // way to stop a one-tap purchase of an unchosen size.
    scope.querySelectorAll('.shopify-payment-button:not([hidden])').forEach((el) => {
      el.setAttribute('hidden', '');
    });
  }

  function release(scope) {
    scope.dataset.mgpSizeChosen = '1';
    scope.querySelectorAll('.is-size-pending').forEach((el) => el.classList.remove('is-size-pending'));
    scope.querySelectorAll('.mgp-size-hint').forEach((el) => el.remove());
    scope.querySelectorAll('.is-size-needed').forEach((el) => el.classList.remove('is-size-needed'));
    scope.querySelectorAll('.shopify-payment-button[hidden]').forEach((el) => el.removeAttribute('hidden'));
  }

  // Add pressed with no size chosen: say so at the size row and take the shopper
  // there. Capture phase on document runs before product-form's own submit
  // listener. Multi-size mode (assets/pdp-flow.js) has no single size to choose,
  // so it is left alone.
  document.addEventListener(
    'submit',
    (event) => {
      // Only a form inside a product's own picker. A card's direct quick-add form
      // has no <product-info>, and falling back to document.body would find the
      // main PDP's size row and block an unrelated add.
      const scope = event.target.closest?.('product-info');
      if (!scope) return;
      const fieldset = scope.querySelector(SIZE_SEL);
      if (!fieldset || scope.dataset.mgpSizeChosen === '1' || scope.dataset.mgpMulti === '1') return;
      if (sizeChoiceCount(fieldset) <= 1) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      fieldset.classList.remove('is-size-needed');
      void fieldset.offsetWidth; // restart the nudge animation on a repeat press
      fieldset.classList.add('is-size-needed');
      if (!fieldset.querySelector('.mgp-size-hint')) {
        const hint = document.createElement('p');
        hint.className = 'mgp-size-hint';
        hint.setAttribute('role', 'alert');
        hint.textContent = 'Choose a size';
        const legend = fieldset.querySelector('legend');
        if (legend) legend.after(hint);
        else fieldset.prepend(hint);
      }
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      fieldset.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      const first = fieldset.querySelector('input:not([disabled]), select');
      if (first) first.focus({ preventScroll: true });
    },
    true
  );

  const gateAll = () => document.querySelectorAll(SIZE_SEL).forEach((el) => gate(scopeOf(el)));

  document.addEventListener('change', (event) => {
    const fieldset = event.target.closest?.(SIZE_SEL);
    if (!fieldset) return;
    release(scopeOf(fieldset));
  });

  // A variant change replaces <variant-selects> with server HTML that has a size
  // pre-checked again. Re-gating on mutation is what makes "pick a colour first"
  // behave the same as landing on the page: still no size, still no add. It is also
  // how the quick-add modal gets gated, since it appears long after load.
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      gateAll();
    });
  }).observe(document.body, { childList: true, subtree: true });

  // An explicit ?variant= link (ad, saved link, trade portal) is a deliberate choice
  // already made — honour it instead of clearing it.
  if (new URLSearchParams(window.location.search).has('variant')) {
    document.querySelectorAll(SIZE_SEL).forEach((el) => release(scopeOf(el)));
  } else {
    gateAll();
  }
})();
